import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  IconButton,
  InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClearIcon from '@mui/icons-material/Clear';
import SearchableSelect from '../../components/SearchableSelect';
import { useSnackbar } from 'notistack';
import vendedorService, { Vendedor } from '../../services/vendedorService';
import LocalidadSearch from '../../components/LocalidadSearch';
import UsuarioSearch from '../../components/UsuarioSearch';
import GrupoMcpSearch from '../../components/GrupoMcpSearch';
import ProveedorSearch from '../../components/ProveedorSearch';
import ImputableSearch from '../../components/ImputableSearch';
import proveedorService, { Proveedor } from '../../services/proveedorService';
import imputableService, { Imputable } from '../../services/imputableService';
import { Usuario } from '../../services/usuarioService';
import { GrupoMcp } from '../../services/grupoMcpService';

const defaultForm: Vendedor = {
  nroVendedor: 0,
  nombreVendedor: '',
  legajo: '',
  tipoVendedor: 'A',
  comisionPorc: 0,
  comisionBase: 'Neto',
  nroProveedor: 0,
  nroCuentaLiquidacion: 0,
  direccion: '',
  codigoCiudad: 0,
  telefono: '',
  remuneracion: 0,
  notas: '',
  lunes: 0,
  martes: 0,
  miercoles: 0,
  jueves: 0,
  viernes: 0,
  sabado: 0,
  domingo: 0,
  horaElun: null,
  horaSlun: null,
  horaEmar: null,
  horaSmar: null,
  horaEmie: null,
  horaSmie: null,
  horaEjue: null,
  horaSjue: null,
  horaEvie: null,
  horaSvie: null,
  horaEsab: null,
  horaSsab: null,
  horaEdom: null,
  horaSdom: null,
  usuario: '',
  sincronizar: 1,
  grupoMcpMobile: 0
};

const tiposVendedor = [
  { value: 'A', label: 'Activo' },
  { value: 'I', label: 'Inactivo' }
];

const VendedorForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codigoVendedor } = useParams();
  const [formData, setFormData] = useState<Vendedor>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [localidadSearchOpen, setLocalidadSearchOpen] = useState(false);
  const [usuarioSearchOpen, setUsuarioSearchOpen] = useState(false);
  const [grupoMcpSearchOpen, setGrupoMcpSearchOpen] = useState(false);
  const [proveedorSearchOpen, setProveedorSearchOpen] = useState(false);
  const [cuentaSearchOpen, setCuentaSearchOpen] = useState(false);
  const [proveedorLabel, setProveedorLabel] = useState('');
  const [cuentaLabel, setCuentaLabel] = useState('');

  const resolveProveedorLabel = useCallback(async (nroProveedor?: number) => {
    const nro = Number(nroProveedor);
    if (!nro || nro <= 0) {
      setProveedorLabel('');
      return;
    }
    try {
      const proveedor = await proveedorService.getProveedor(nro);
      setProveedorLabel(`${proveedor.nroProveedor} - ${(proveedor.nombre ?? '').trim()}`);
    } catch {
      setProveedorLabel(String(nro));
    }
  }, []);

  const resolveCuentaLabel = useCallback(async (nroCuenta?: number) => {
    const nro = Number(nroCuenta);
    if (!nro || nro <= 0) {
      setCuentaLabel('');
      return;
    }
    try {
      const cuenta = await imputableService.getImputable(nro);
      if (cuenta) {
        setCuentaLabel(`${cuenta.nroCuenta} - ${(cuenta.nomCuenta ?? '').trim()}`);
      } else {
        setCuentaLabel(String(nro));
      }
    } catch {
      setCuentaLabel(String(nro));
    }
  }, []);

  const loadVendedor = useCallback(async () => {
    if (!codigoVendedor || codigoVendedor === 'nuevo') return;
    try {
      setLoading(true);
      const vendedor = await vendedorService.getVendedor(Number(codigoVendedor));
      setFormData(vendedor);
      await Promise.all([
        resolveProveedorLabel(vendedor.nroProveedor),
        resolveCuentaLabel(vendedor.nroCuentaLiquidacion),
      ]);
    } catch (error) {
      enqueueSnackbar('Error al cargar el vendedor', { variant: 'error' });
      navigate('/ventas/vendedores');
    } finally {
      setLoading(false);
    }
  }, [codigoVendedor, enqueueSnackbar, navigate, resolveProveedorLabel, resolveCuentaLabel]);

  useEffect(() => {
    if (codigoVendedor && codigoVendedor !== 'nuevo') {
      loadVendedor();
    }
  }, [codigoVendedor, loadVendedor]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericFields = [
      'nroVendedor',
      'comisionPorc',
      'codigoCiudad',
      'dni',
      'remuneracion',
      'lunes',
      'martes',
      'miercoles',
      'jueves',
      'viernes',
      'sabado',
      'domingo',
      'turno',
      'comisionServicio',
      'nroProveedor',
      'nroCuentaLiquidacion',
      'sincronizar',
      'grupoMcpMobile',
    ];

    if (numericFields.includes(name)) {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleProveedorSelect = (proveedor: Proveedor) => {
    setFormData(prev => ({ ...prev, nroProveedor: proveedor.nroProveedor }));
    setProveedorLabel(`${proveedor.nroProveedor} - ${(proveedor.nombre ?? '').trim()}`);
    setProveedorSearchOpen(false);
  };

  const handleClearProveedor = () => {
    setFormData(prev => ({ ...prev, nroProveedor: 0 }));
    setProveedorLabel('');
  };

  const handleCuentaSelect = (imputable: Imputable) => {
    setFormData(prev => ({ ...prev, nroCuentaLiquidacion: Number(imputable.nroCuenta) }));
    setCuentaLabel(`${imputable.nroCuenta} - ${(imputable.nomCuenta ?? '').trim()}`);
    setCuentaSearchOpen(false);
  };

  const handleClearCuenta = () => {
    setFormData(prev => ({ ...prev, nroCuentaLiquidacion: 0 }));
    setCuentaLabel('');
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked ? 1 : 0 }));
  };

  const handleLocalidadSelect = (localidad: any) => {
    setFormData(prev => ({
      ...prev,
      codigoCiudad: localidad.codigoCiudad
    }));
    setLocalidadSearchOpen(false);
  };

  const handleUsuarioSelect = (usuario: Usuario) => {
    const usuarioId = String(usuario.usuario || usuario.username || usuario.id || '').trim();
    setFormData(prev => ({
      ...prev,
      usuario: usuarioId
    }));
    setUsuarioSearchOpen(false);
  };

  const handleGrupoMcpSelect = (grupoMcp: GrupoMcp) => {
    setFormData(prev => ({
      ...prev,
      grupoMcpMobile: grupoMcp.grupoMcp
    }));
    setGrupoMcpSearchOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.nombreVendedor.trim()) {
        enqueueSnackbar('El nombre del vendedor es requerido', { variant: 'warning' });
        return;
      }

      if (codigoVendedor && codigoVendedor !== 'nuevo') {
        await vendedorService.updateVendedor(Number(codigoVendedor), formData);
      } else {
        await vendedorService.createVendedor(formData);
      }

      enqueueSnackbar('Vendedor guardado exitosamente', { variant: 'success' });
      navigate('/ventas/vendedores');
    } catch (error: any) {
      console.error('Error al guardar vendedor:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar el vendedor', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/ventas/vendedores')}
              sx={{ textTransform: 'none' }}
            >
              Vendedores
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {codigoVendedor && codigoVendedor !== 'nuevo' ? 'Editar Vendedor' : 'Nuevo Vendedor'}
            </Typography>
          </Stack>

          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
            <Tab label="General" />
            <Tab label="Horarios" />
            <Tab label="Comisiones" />
            <Tab label="App Movil" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="nroVendedor" 
                    label="N° Vendedor" 
                    type="number"
                    value={formData.nroVendedor} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                    disabled={!!codigoVendedor && codigoVendedor !== 'nuevo'}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={9}>
                  <TextField 
                    name="nombreVendedor" 
                    label="Nombre" 
                    value={formData.nombreVendedor} 
                    onChange={handleChange} 
                    fullWidth 
                    required
                    inputProps={{ maxLength: 20 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="legajo" 
                    label="Legajo" 
                    value={formData.legajo || ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ maxLength: 20 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <SearchableSelect
                    name="tipoVendedor"
                    label="Tipo Vendedor"
                    value={formData.tipoVendedor || 'A'}
                    onChange={(v) => setFormData(prev => ({ ...prev, tipoVendedor: v ?? 'A' }))}
                    options={tiposVendedor}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    name="direccion" 
                    label="Dirección" 
                    value={formData.direccion || ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ maxLength: 25 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1}>
                    <TextField 
                      label="Localidad" 
                      value={formData.codigoCiudad || ''} 
                      fullWidth
                      disabled
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button 
                      variant="outlined" 
                      onClick={() => setLocalidadSearchOpen(true)}
                      sx={{ minWidth: 120 }}
                    >
                      Buscar
                    </Button>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="telefono" 
                    label="Teléfono" 
                    value={formData.telefono || ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ maxLength: 25 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    name="notas" 
                    label="Notas" 
                    value={formData.notas || ''} 
                    onChange={handleChange} 
                    fullWidth
                    multiline
                    rows={3}
                    inputProps={{ maxLength: 5000 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Horarios de Trabajo</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaElun" 
                    label="Lunes - Entrada" 
                    type="time"
                    value={formData.horaElun || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaSlun" 
                    label="Lunes - Salida" 
                    type="time"
                    value={formData.horaSlun || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaEmar" 
                    label="Martes - Entrada" 
                    type="time"
                    value={formData.horaEmar || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaSmar" 
                    label="Martes - Salida" 
                    type="time"
                    value={formData.horaSmar || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaEmie" 
                    label="Miércoles - Entrada" 
                    type="time"
                    value={formData.horaEmie || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaSmie" 
                    label="Miércoles - Salida" 
                    type="time"
                    value={formData.horaSmie || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaEjue" 
                    label="Jueves - Entrada" 
                    type="time"
                    value={formData.horaEjue || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaSjue" 
                    label="Jueves - Salida" 
                    type="time"
                    value={formData.horaSjue || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaEvie" 
                    label="Viernes - Entrada" 
                    type="time"
                    value={formData.horaEvie || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="horaSvie" 
                    label="Viernes - Salida" 
                    type="time"
                    value={formData.horaSvie || ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="comisionPorc" 
                    label="% Comisión Ventas" 
                    type="number"
                    value={formData.comisionPorc || ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ step: '0.01', min: '0', max: '100' }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <SearchableSelect
                    name="comisionBase"
                    label="Base Comisión Ventas"
                    value={formData.comisionBase || 'Neto'}
                    onChange={(v) => setFormData(prev => ({ ...prev, comisionBase: v ?? 'Neto' }))}
                    options={[
                      { value: 'Neto', label: 'Importe Neto' },
                      { value: 'Importe total', label: 'Importe Total' },
                    ]}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="remuneracion" 
                    label="Remuneración" 
                    type="number"
                    value={formData.remuneracion || ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ step: '0.01', min: '0' }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Nº Proveedor Asociado"
                    value={
                      proveedorLabel ||
                      (Number(formData.nroProveedor) > 0 ? String(formData.nroProveedor) : '')
                    }
                    fullWidth
                    helperText="Proveedor al que se generará la Factura Interna X al liquidar comisiones"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          {Number(formData.nroProveedor) > 0 && (
                            <IconButton
                              onClick={handleClearProveedor}
                              edge="end"
                              size="small"
                              title="Quitar proveedor"
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            onClick={() => setProveedorSearchOpen(true)}
                            edge="end"
                            color="primary"
                            title="Buscar proveedor"
                          >
                            <ArrowDropDownIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Cuenta Contable de Comisión"
                    value={
                      cuentaLabel ||
                      (Number(formData.nroCuentaLiquidacion) > 0
                        ? String(formData.nroCuentaLiquidacion)
                        : '')
                    }
                    fullWidth
                    helperText="Cuenta imputada en la Factura X al liquidar (ej. Comisiones Otorgadas)"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          {Number(formData.nroCuentaLiquidacion) > 0 && (
                            <IconButton
                              onClick={handleClearCuenta}
                              edge="end"
                              size="small"
                              title="Quitar cuenta"
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            onClick={() => setCuentaSearchOpen(true)}
                            edge="end"
                            color="primary"
                            title="Buscar cuenta contable"
                          >
                            <ArrowDropDownIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 3 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 1 }}>Configuración App Movil</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Number(formData.sincronizar || 0) === 1}
                        onChange={handleSwitchChange}
                        name="sincronizar"
                        color="primary"
                      />
                    }
                    label="Sincronizar"
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      name="usuario"
                      label="Usuario"
                      value={formData.usuario || ''}
                      fullWidth
                      disabled
                      inputProps={{ maxLength: 50 }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => setUsuarioSearchOpen(true)}
                      sx={{ minWidth: 120 }}
                    >
                      Buscar
                    </Button>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      label="GrupoMCP_Mobile"
                      value={formData.grupoMcpMobile || ''}
                      fullWidth
                      disabled
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => setGrupoMcpSearchOpen(true)}
                      sx={{ minWidth: 120 }}
                    >
                      Buscar
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/ventas/vendedores')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <LocalidadSearch
        open={localidadSearchOpen}
        onClose={() => setLocalidadSearchOpen(false)}
        onSelect={handleLocalidadSelect}
      />
      <UsuarioSearch
        open={usuarioSearchOpen}
        onClose={() => setUsuarioSearchOpen(false)}
        onSelect={handleUsuarioSelect}
      />
      <GrupoMcpSearch
        open={grupoMcpSearchOpen}
        onClose={() => setGrupoMcpSearchOpen(false)}
        onSelect={handleGrupoMcpSelect}
      />
      <ProveedorSearch
        open={proveedorSearchOpen}
        onClose={() => setProveedorSearchOpen(false)}
        onSelect={handleProveedorSelect}
      />
      <ImputableSearch
        open={cuentaSearchOpen}
        onClose={() => setCuentaSearchOpen(false)}
        onSelect={handleCuentaSelect}
      />
    </Box>
  );
};

export default VendedorForm;
